/**
 * Terrain Chunk - manages a grid of tiles using InstancedMesh for performance
 * Optimized: Uses single InstancedMesh instead of individual meshes per tile
 */

import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { tileHeight, tileColor, isLand, isBeach, TERRAIN_HEIGHT_MULTIPLIER } from './TerrainGenerator';
import type { GameConfig } from '@/types';
import { DEFAULT_CONFIG } from '@/types';

// Use shared height multiplier for consistency
const HEIGHT_MULTIPLIER = TERRAIN_HEIGHT_MULTIPLIER;

// Shared geometry (created once, reused across all chunks)
let sharedTileGeometry: RoundedBoxGeometry | null = null;

function getTileGeometry(tileSize: number): RoundedBoxGeometry {
  if (!sharedTileGeometry) {
    // Unit-sized geometry, will be scaled per instance
    sharedTileGeometry = new RoundedBoxGeometry(tileSize, 1, tileSize, 2, 0.02);
  }
  return sharedTileGeometry;
}

// Tile data for queries (selection, pathfinding, etc.)
interface TileData {
  tileX: number;
  tileZ: number;
  worldX: number;
  worldZ: number;
  height: number;
  isLand: boolean;
  isBeach: boolean;
  instanceIndex: number;
}

export class Chunk {
  public readonly cx: number;
  public readonly cz: number;
  public readonly group: THREE.Group;

  private config: GameConfig;
  private instancedMesh: THREE.InstancedMesh | null = null;
  private tileDataMap: Map<string, TileData> = new Map(); // key: "wx,wz"
  private tileCount: number = 0;
  private dummy = new THREE.Object3D();

  constructor(cx: number, cz: number, config: GameConfig = DEFAULT_CONFIG) {
    this.cx = cx;
    this.cz = cz;
    this.config = config;
    this.group = new THREE.Group();

    this.build();
  }

  key(): string {
    return `${this.cx},${this.cz}`;
  }

  private build(): void {
    const { chunkSize, tileSize } = this.config;
    const startX = this.cx * chunkSize;
    const startZ = this.cz * chunkSize;

    // First pass: count land tiles to allocate exact instance count
    const tileInfos: {
      gx: number;
      gz: number;
      wx: number;
      wz: number;
      h: number;
      isBeachTile: boolean;
    }[] = [];

    for (let z = 0; z < chunkSize; z++) {
      for (let x = 0; x < chunkSize; x++) {
        const gx = startX + x;
        const gz = startZ + z;
        const wx = (gx + 0.5) * tileSize;
        const wz = (gz + 0.5) * tileSize;
        const h = tileHeight(wx, wz);

        if (isLand(h)) {
          tileInfos.push({
            gx,
            gz,
            wx,
            wz,
            h,
            isBeachTile: isBeach(wx, wz, h),
          });
        }
      }
    }

    this.tileCount = tileInfos.length;
    if (this.tileCount === 0) return;

    // Create instanced mesh with exact count needed
    const geometry = getTileGeometry(tileSize);
    const material = new THREE.MeshStandardMaterial({
      roughness: 0.8,
      metalness: 0.1,
    });

    this.instancedMesh = new THREE.InstancedMesh(geometry, material, this.tileCount);
    this.instancedMesh.castShadow = true;
    this.instancedMesh.receiveShadow = true;

    // Second pass: set instance transforms and colors
    for (let i = 0; i < tileInfos.length; i++) {
      const { gx, gz, wx, wz, h, isBeachTile } = tileInfos[i];

      // Calculate height visualization
      const hVis = isBeachTile ? h * 0.8 : h * HEIGHT_MULTIPLIER;
      const yPos = isBeachTile ? h * 0.3 : h * HEIGHT_MULTIPLIER * 0.5;

      // Set transform
      this.dummy.position.set(wx, yPos, wz);
      this.dummy.scale.set(1, Math.max(0.3, hVis), 1);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.instancedMesh.setMatrixAt(i, this.dummy.matrix);

      // Set color using Three.js instanceColor system
      const color = tileColor(wx, wz, h);
      this.instancedMesh.setColorAt(i, color);

      // Store tile data for queries
      const key = `${Math.floor(wx)},${Math.floor(wz)}`;
      this.tileDataMap.set(key, {
        tileX: gx,
        tileZ: gz,
        worldX: wx,
        worldZ: wz,
        height: h,
        isLand: true,
        isBeach: isBeachTile,
        instanceIndex: i,
      });
    }

    // Apply instance attributes
    this.instancedMesh.instanceMatrix.needsUpdate = true;
    if (this.instancedMesh.instanceColor) {
      this.instancedMesh.instanceColor.needsUpdate = true;
    }

    this.group.add(this.instancedMesh);
  }

  dispose(): void {
    if (this.instancedMesh) {
      // Don't dispose shared geometry
      if (this.instancedMesh.material instanceof THREE.Material) {
        this.instancedMesh.material.dispose();
      }
      this.instancedMesh = null;
    }
    this.tileDataMap.clear();
  }

  getTileAt(wx: number, wz: number): TileData | null {
    const { tileSize } = this.config;
    // Find the tile that contains this world position
    for (const data of this.tileDataMap.values()) {
      const dx = Math.abs(data.worldX - wx);
      const dz = Math.abs(data.worldZ - wz);
      if (dx < tileSize * 0.5 && dz < tileSize * 0.5) {
        return data;
      }
    }
    return null;
  }

  /**
   * Update a tile's color (for territory changes, selection highlighting, etc.)
   */
  updateTileColor(wx: number, wz: number, color: THREE.Color): void {
    const tile = this.getTileAt(wx, wz);
    if (!tile || !this.instancedMesh) return;

    this.instancedMesh.setColorAt(tile.instanceIndex, color);
    if (this.instancedMesh.instanceColor) {
      this.instancedMesh.instanceColor.needsUpdate = true;
    }
  }

  /**
   * Get the instanced mesh (for raycasting, etc.)
   */
  getInstancedMesh(): THREE.InstancedMesh | null {
    return this.instancedMesh;
  }

  /**
   * Get tile data by instance ID (used after raycasting)
   */
  getTileByInstanceId(instanceId: number): TileData | null {
    for (const data of this.tileDataMap.values()) {
      if (data.instanceIndex === instanceId) {
        return data;
      }
    }
    return null;
  }
}
